import { useState } from 'react';
import { computeVisibility, type AnswerMap } from '@huaca/claro-engine';
import type { Field, TemplateBody } from '../types';

/**
 * Live, interactive preview of the template exactly as a client will see it — driven by the SAME
 * `conditions.ts` engine the Room uses, so an Editor can fill fields and watch conditions show/hide
 * sections and fields in real time.
 */
export function TemplatePreview({ body }: { body: TemplateBody }) {
  const [answers, setAnswers] = useState<AnswerMap>({});
  const { visibleFieldIds, visibleSectionIds } = computeVisibility(body, answers);
  const set = (fieldId: string, value: string) => setAnswers((a) => ({ ...a, [fieldId]: { value } }));

  const visibleSections = body.sections.filter((s) => visibleSectionIds.has(s.id));
  if (visibleSections.length === 0) return <p className="claro-hint">הוסיפו מקטעים ושדות כדי לראות תצוגה מקדימה.</p>;

  return (
    <div className="claro-preview" data-testid="template-preview">
      {visibleSections.map((section) => (
        <div key={section.id} className="claro-preview-section">
          <h4>{section.title || '(מקטע ללא שם)'}</h4>
          {section.fields
            .filter((f) => visibleFieldIds.has(f.id))
            .map((field) => (
              <div key={field.id} className="huaca-field">
                <label>
                  {field.label || '(שדה ללא שם)'}
                  {field.required && <span className="claro-req"> *</span>}
                </label>
                <PreviewInput field={field} value={answers[field.id]?.value} onChange={(v) => set(field.id, v)} />
              </div>
            ))}
        </div>
      ))}
    </div>
  );
}

function PreviewInput({
  field,
  value,
  onChange,
}: {
  field: Field;
  value: string | number | null | undefined;
  onChange: (v: string) => void;
}) {
  const v = value == null ? '' : String(value);
  switch (field.type) {
    case 'select':
      return (
        <select value={v} onChange={(e) => onChange(e.target.value)}>
          <option value="">— בחרו —</option>
          {(field.options ?? []).map((o, i) => (
            <option key={i} value={o.value}>
              {o.label || o.value}
            </option>
          ))}
        </select>
      );
    case 'number':
      return <input type="number" dir="ltr" value={v} onChange={(e) => onChange(e.target.value)} />;
    case 'date':
      return <input type="date" dir="ltr" value={v} onChange={(e) => onChange(e.target.value)} />;
    case 'file':
      return <input type="file" disabled />;
    default:
      return <input value={v} onChange={(e) => onChange(e.target.value)} />;
  }
}
